import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { startLogin } from "@/lib/startLogin";

const landingPath = (prefs) => (prefs?.landing === "calendar" ? "/calendar" : "/dashboard");

const PILLARS = [
  ["Sessions, not slots", "Fixed-time listings, session flags, travel days and post-court work."],
  ["Every date on record", "Hearing logs, orders and attachments hanging off each matter."],
  ["Carried to court", "Printable day sheets, associate briefs and reminders that reach you."],
];

export default function Landing() {
  const { user, loading } = useAuth();
  if (!loading && user) return <Navigate to={landingPath(user.preferences)} replace />;

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div
        className="texture-layer"
        style={{
          backgroundImage: "url(/chambers-logo.png)",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "115% 50%",
          backgroundSize: "min(80vh, 46rem)",
          opacity: 0.12,
        }}
      />

      <main className="relative z-10 mx-auto max-w-5xl px-6 min-h-screen flex flex-col justify-center py-16">
        <img
          src="/chambers-logo.png"
          alt="Chambers of Amir Arsiwala"
          className="h-32 w-32 sm:h-40 sm:w-40 object-contain -ml-2"
          data-testid="landing-logo"
        />
        <p className="label-caps mt-8">Private diary · by invitation only</p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.04] mt-4 max-w-3xl">
          Chambers of Amir Arsiwala
        </h1>
        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          The court diary, case record and daily brief of these chambers. Sign in with the Google
          account on the chambers access list.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Button
            data-testid="get-started-btn"
            onClick={startLogin}
            className="rounded-full h-14 px-9 text-base font-semibold"
          >
            Sign in with Google
          </Button>
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="h-4 w-4" />
            Access is limited to whitelisted email IDs
          </span>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map(([title, body], i) => (
            <div
              key={title}
              className="surface p-6 rise"
              style={{ animationDelay: `${i * 70}ms` }}
              data-testid={`landing-feature-${i}`}
            >
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
